import React, { useRef, useEffect, useState } from 'react';

/**
 * Reveal — the only motion allowed sitewide.
 * Default: subtle fade + 12px rise. Optional direction: 'left' | 'right' slides
 * in horizontally. All variants disabled/reduced-fade under prefers-reduced-motion
 * (handled in CSS). Triggers ONCE when the element's edge enters view.
 *
 * 2026-09-01 bug fix: threshold 0.25 could never be satisfied by elements
 * taller than ~4x the viewport (e.g. the whole sp-body-inner wrapper on
 * service pages — the entire page body stayed invisible forever). Now
 * threshold 0 (edge trigger, bottom inset keeps it off the sticky bar) PLUS
 * a 300ms in-viewport fallback and a no-IntersectionObserver fallback, so no
 * section can ever be stuck hidden on any page.
 */
const Reveal = ({
  children,
  as: Tag = 'div',
  className = '',
  delay = 0,
  direction, // 'left' | 'right' | undefined (=up)
  onFocusCapture,
  style,
  ...props
}) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [revealedByFocus, setRevealedByFocus] = useState(false);

  const handleFocusCapture = (event) => {
    // Keyboard focus must never wait for the observer, stagger or fade.
    setRevealedByFocus(true);
    setVisible(true);
    onFocusCapture?.(event);
  };

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!('IntersectionObserver' in window)) { setVisible(true); return; }

    // Same obstruction-aware trigger the counter uses — nothing reveals
    // while hidden behind the sticky mobile call bar. On desktop the bar
    // isn't rendered, so this falls back to a small early-trigger inset.
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    let bottom = 40;
    if (isMobile) {
      const bar = document.querySelector('[data-testid=sticky-bar]');
      bottom = bar ? Math.round(bar.getBoundingClientRect().height + 16) : 120;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0, rootMargin: `0px 0px ${-bottom}px 0px` }
    );
    observer.observe(el);

    // Safeguard: if the observer hasn't fired within 300ms while the element
    // is in the viewport, force it visible anyway — nothing may stay hidden.
    const t = setTimeout(() => {
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) {
        setVisible(true);
        observer.unobserve(el);
      }
    }, 300);

    return () => { observer.disconnect(); clearTimeout(t); };
  }, []);

  const dirCls = direction === 'left' ? 'reveal-from-left' : direction === 'right' ? 'reveal-from-right' : '';

  return (
    <Tag
      ref={ref}
      className={`reveal ${dirCls} ${visible ? 'reveal-visible' : ''} ${className}`.trim()}
      style={{ transitionDelay: `${delay}ms`, ...style,
        ...(revealedByFocus ? { transition: 'none', transitionDelay: '0ms' } : {}) }}
      onFocusCapture={handleFocusCapture}
      // Pre-rendered pages capture above-the-fold Reveals already visible
      // (so the static HTML paints content immediately — LCP). The client's
      // first render starts hidden, which is an intentional, recoverable
      // class difference: React keeps the server's visible class, the effect
      // below then confirms visible=true. Without this flag React would log
      // a hydration warning for every above-fold Reveal.
      suppressHydrationWarning
      {...props}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
